import React, { useEffect, useState } from "react";
import { Alert, Button, Collapse, Drawer, Empty, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Spin, Tabs, Tag, message } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import http from "@/utils/http";
import api from "@/utils/api";

const TITLES = { group: "Group", list: "List", summary: "Summary", total: "Total" };

/** Edits the shared RES template; optimistic revisions protect concurrent editors. */
export default function ReportCreator({ open, onClose, onSaved }) {
  const [rules, setRules] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const accept = (response) => {
    if (String(response?.code) !== "1") throw new Error(response?.msg || "Unable to update RES report rules.");
    setRules(response.data);
  };

  const load = async () => {
    setLoading(true);
    try { accept(await http.get(api.getResReportRules)); }
    catch (error) { message.error(error.message || "Unable to load RES report rules."); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    http.get(api.getResReportRules).then((response) => {
      if (active) accept(response);
    }).catch((error) => { if (active) message.error(error.message || "Unable to load RES report rules."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open]);

  const edit = (rule) => {
    const kind = rule.kind;
    const values = { displayAs: kind === "group" ? -1 : 1, sortOrder: 255, isGroup: 0, benchmark: 0, ...rule };
    setEditing(values);
    form.resetFields();
    form.setFieldsValue(values);
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      accept(await http.post(api.saveResReportRule, { ...editing, ...values, revision: rules.revision }));
      setEditing(null);
      message.success("RES report rule saved. Run the report to apply the changes.");
      onSaved();
    } catch (error) {
      if (!error.errorFields) message.error(error.message || "Unable to save the rule.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (rule) => {
    setSaving(true);
    try {
      accept(await http.post(api.deleteResReportRule, { kind: rule.kind, id: rule.id, revision: rules.revision }));
      onSaved();
      message.success("Rule removed.");
    } catch (error) {
      message.error(error.message || "Unable to remove the rule.");
    } finally {
      setSaving(false);
    }
  };

  const actions = (rule) => <Space size={4} onClick={(event) => event.stopPropagation()}>
    <Button size="small" type="text" aria-label={`Edit ${rule.name}`} icon={<EditOutlined />} disabled={saving} onClick={() => edit(rule)} />
    <Popconfirm title={rule.kind === "group" ? "Delete this group and its lists?" : "Delete this rule?"} onConfirm={() => remove(rule)} okText="Delete" okButtonProps={{ danger: true }}>
      <Button size="small" type="text" danger aria-label={`Delete ${rule.name}`} disabled={saving} icon={<DeleteOutlined />} />
    </Popconfirm>
  </Space>;

  const groupOptions = (rules?.group || []).map((group) => ({ value: group.id, label: group.name }));
  const ruleCard = (rule) => <div key={rule.id} className="res-report-rule-line"><div><strong>{rule.name}</strong><small>Order {rule.sortOrder}{rule.glList ? ` · GL: ${rule.glList}` : ""}{rule.glListAdd ? ` · Add: ${rule.glListAdd}` : ""}{rule.glListSub ? ` · Subtract: ${rule.glListSub}` : ""}</small></div>{actions(rule)}</div>;
  const groups = <>
    <div className="res-report-rules-actions"><Button icon={<PlusOutlined />} onClick={() => edit({ kind: "group" })}>New Group</Button><Button icon={<PlusOutlined />} disabled={!groupOptions.length} onClick={() => edit({ kind: "list", parentGroupId: groupOptions[0]?.value })}>New List</Button></div>
    <Collapse className="res-report-rules-groups">{(rules?.group || []).map((group) => <Collapse.Panel key={group.id} header={<span>{group.name} <Tag>× {group.displayAs}</Tag><small>Order {group.sortOrder}</small></span>} extra={actions(group)}>
      {(rules?.list || []).filter((line) => line.parentGroupId === group.id).map(ruleCard)}
      <Button type="dashed" block icon={<PlusOutlined />} onClick={() => edit({ kind: "list", parentGroupId: group.id })}>Add list to this group</Button>
    </Collapse.Panel>)}</Collapse>
    {!rules?.group?.length && <Empty description="No groups configured" />}
  </>;

  return <>
    <Drawer title="RES · Report Creator" open={open} onClose={onClose} width={Math.min(820, window.innerWidth)} extra={<Button icon={<ReloadOutlined />} loading={loading} onClick={load}>Refresh</Button>}>
      <Alert type="info" showIcon message="Saved rules update the shared RES template." description="Groups and lists control this report. Summary and Total settings are retained for compatibility with the legacy Report Creator." />
      <Spin spinning={loading || saving}><Tabs items={[
        { key: "group", label: "Groups & Lists", children: groups },
        ...["summary", "total"].map((kind) => ({ key: kind, label: kind === "summary" ? "Summaries" : "Totals", children: <><div className="res-report-rules-actions"><Button icon={<PlusOutlined />} onClick={() => edit({ kind })}>New {TITLES[kind]}</Button></div>{rules?.[kind]?.length ? rules[kind].map(ruleCard) : <Empty description={`No ${kind} rules configured`} />}</> })),
      ]} /></Spin>
    </Drawer>
    <Modal title={`${editing?.id ? "Edit" : "New"} ${TITLES[editing?.kind] || "Rule"}`} open={Boolean(editing)} onCancel={() => setEditing(null)} onOk={save} confirmLoading={saving} okText="Save rule" width={620}>
      <Form form={form} layout="vertical">
        <Form.Item name="name" label="Name" rules={[{ required: true, whitespace: true, max: 255 }]}><Input maxLength={255} /></Form.Item>
        <Form.Item name="sortOrder" label="Display order" rules={[{ required: true }]}><InputNumber min={0} max={32767} precision={0} /></Form.Item>
        {editing?.kind === "group" && <Form.Item name="displayAs" label="Amount multiplier" rules={[{ required: true }]}><Select options={[{ value: -1, label: "× -1 (reverse sign)" }, { value: 1, label: "× 1 (original sign)" }]} /></Form.Item>}
        {editing?.kind === "list" && <>
          <Form.Item name="parentGroupId" label="Group" rules={[{ required: true }]}><Select options={groupOptions} /></Form.Item>
          <Form.Item name="glList" label="GL accounts / ranges" rules={[{ required: true, whitespace: true }]}><Input.TextArea rows={5} placeholder="601-6049999, 390" /></Form.Item>
          <Form.Item name="acctTypeList" label="Account types"><Input placeholder="Comma or ~ separated account types" /></Form.Item>
          <Form.Item name="isGroup" label="Group list"><Select options={[{ value: 0, label: "No" }, { value: 1, label: "Yes" }]} /></Form.Item>
        </>}
        {editing?.kind === "summary" && <>
          <Form.Item name="glListAdd" label="GL accounts to add" rules={[{ required: true }]}><Input.TextArea rows={3} /></Form.Item>
          <Form.Item name="glListSub" label="GL accounts to subtract"><Input.TextArea rows={3} /></Form.Item>
          <Form.Item name="benchmark" label="Benchmark"><Select options={[{ value: 0, label: "No" }, { value: 1, label: "Yes" }]} /></Form.Item>
          <Form.Item name="displayAs" label="Amount multiplier"><Select options={[{ value: -1, label: "× -1" }, { value: 1, label: "× 1" }]} /></Form.Item>
        </>}
        {editing?.kind === "total" && <Form.Item name="position" label="After group" rules={[{ required: true }]}><Select options={groupOptions} /></Form.Item>}
      </Form>
    </Modal>
  </>;
}
